import { Account } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import { BreadcrumbProps, Button, Card, CardBody, CardHeader, DateDisplay, Icon, NumberDisplay, PieChart, Select, SelectOptionType, Tooltip } from '@aidkitorg/component-library';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useAccounts } from '../../Contexts/AccountsContext';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { useVisibilityPolling } from '../../Hooks/VisibilityPolling';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { AccountDetailsCard } from './components/AccountDetailsCard';
import { ManagePaymentRemindersModal } from './components/ManagePaymentRemindersModal';
import { useBalancesConfig } from '../shared/hooks/useAdminPanelPageConfig';

export const accountKindToName = (kind: Account['kind']): string => {
  switch(kind) {
    case 'usio':
      return 'USIO (Debit)'
    case 'dwolla':
      return 'Dwolla (ACH)'
    case 'mercury':
      return 'Mercury Account'
    case 'usbank':
      return 'US Bank (Debit)'
    case 'givecard':
      return 'Givecard'
    case 'givecard_rtp':
      return 'Givecard (ACH)'
    case 'givecard_debit':
      return "Givecard (Debit)"
    case 'unknown':
      return "Unknown"
  }
}

export const AccountsPage = () => {
  const L = useLocalizedStrings()

  const [processedAccounts, setProcessedAccounts] = useState<Array<Account>>([])
  const [manageBalanceRemindersModalOpen, setManageBalanceRemindersModalOpen] = useState<boolean>(false)
  const [selectedSubledger, setSelectedSubledger] = useState<SelectOptionType>({ value: 'all', label: L.admin_panel.accounts.all_subledgers })

  // Fetch page configuration using the hook
  const {
    pageConfig,
    isVisible
  } = useBalancesConfig();

  // Use config to determine what to show
  const showTotalAvailableBalance = isVisible(['totalAvailableBalance']);
  const showPaymentProviderShareChart = isVisible(['paymentProviderShareChart']);
  const showSubledgers = isVisible(['subledgers']);
  const showManageRemindersButton = isVisible(['manageRemindersButton']);

  // Get the actual subledger value
  const selectedSubledgerValue = selectedSubledger.value as string

  const location = useLocation()
  const history = useHistory()
  const isOnAccountsPage = location.pathname === '/admin-panel/balances'
  
  // Check for URL parameters to auto-open modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('action') === 'manage-reminders') {
      setManageBalanceRemindersModalOpen(true)
      
      // Clean up the URL parameter after opening the modal
      searchParams.delete('action')
      const newSearch = searchParams.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname
      history.replace(newUrl)
    }
  }, [location.search, location.pathname, history])
    
  const {
    totalBalance,
    accounts,
    subledgers,
    accountsDataTimestamp,
    isAccountsLoading,
    fetchAccountsOverview
  } = useAccounts()  
  
  useAsyncEffect(async () => {
    await fetchAccountsOverview()
  }, [])

  useVisibilityPolling({
    onImmediate: () => fetchAccountsOverview(),
    onInterval: () => fetchAccountsOverview(),
    isActive: isOnAccountsPage,
    intervalMs: 2 * 60 * 1000
  })

  // Calculate balances based on selected subledger
  const { displayTotalBalance, displayAccounts } = useMemo(() => {
    if (selectedSubledgerValue === 'all') {
      return {
        displayTotalBalance: totalBalance,
        displayAccounts: accounts.map((account) => ({
          ...account,
          name: accountKindToName(account.kind),
          totalBalance: account.totalBalance/100
        }))
      }
    }

    // Filter for specific subledger
    let subledgerTotal = 0
    const subledgerAccounts = accounts.map((account) => {
      const subledgerBalance = account.subledgers?.[selectedSubledgerValue] || 0
      subledgerTotal += subledgerBalance
      return {
        ...account,
        name: accountKindToName(account.kind),
        totalBalance: subledgerBalance
      }
    })

    return {
      displayTotalBalance: subledgerTotal,
      displayAccounts: subledgerAccounts
    }
  }, [selectedSubledgerValue, totalBalance, accounts])

  useEffect(() => {
    setProcessedAccounts(displayAccounts)
  }, [displayAccounts])

  const totalBalanceCard = <NumberDisplay
    title={L.admin_panel.accounts.total_available_balance}
    isCurrency={true}
    locale="en-US"
    currency="USD"
    iconColor='blue'
    iconName='WalletIcon'
    className="w-full h-full"
    value={displayTotalBalance ? 0.01 * displayTotalBalance : undefined }
    loading={isAccountsLoading}
  />

  const accountsPieChartCard = <Card>
    <CardHeader title={<div className="font-medium">{L.admin_panel.accounts.payment_provider_share}</div>}/>
    <CardBody>
      <PieChart 
        currency
        data={processedAccounts} 
        nameKey='name' 
        dataKey='totalBalance' 
        width="50%" 
        isLoading={isAccountsLoading}
        height={isAccountsLoading ? 300 : 350}
        showLegend
        showLabels={false}
      />
    </CardBody>
  </Card>

  const accountDetailsCards = processedAccounts
    .sort((a, b) => b.totalBalance - a.totalBalance)
    .map((account, idx) => {
      return (
        <div key={idx}>
          <AccountDetailsCard index={idx} account={account} className="h-full"/>
        </div>
      )
    })

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.accounting,
    },
    {
      label: L.admin_panel.nav.balances
    }
  ];

  // Prepare subledger options for the Select dropdown
  const subledgerOptions = useMemo(() => {
    const options = [{ value: 'all', label: L.admin_panel.accounts.all_subledgers }]

    // Sort subledgers: 'default' first, then alphabetically
    const sortedSubledgers = [...subledgers].sort((a, b) => {
      if (a === 'default') return -1
      if (b === 'default') return 1
      return a.localeCompare(b)
    })

    sortedSubledgers.forEach(subledger => {
      // Convert snake_case to Title Case
      const label = subledger
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      options.push({
        value: subledger,
        label: label
      })
    })
    return options
  }, [subledgers, L.admin_panel.accounts.all_subledgers])

  // Build action buttons based on config
  const actionButtons = [
    <Tooltip key="refresh" text={L.admin_panel.payments.refresh_data} asChild>
      <Button
        icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
        variant="secondary"
        aria-label={L.admin_panel.payments.refresh_data}
        onClick={async () => { await fetchAccountsOverview(true) }}
      />
    </Tooltip>
  ];

  if (showManageRemindersButton) {
    actionButtons.push(
      <Button
        key="manage-reminders"
        variant="primary"
        icon={<Icon name="CalendarIcon" type="solid" role="decorative" />}
        onClick={() => setManageBalanceRemindersModalOpen(true)}
        aria-label={L.admin_panel.accounts.manage_reminders}
      >
        {L.admin_panel.accounts.manage_reminders}
      </Button>
    );
  }

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.balances}
      breadcrumbs={breadcrumbs}
      pageActionButtons={actionButtons}
    >
      <div className="flex flex-col gap-2 w-full h-full">
        {showSubledgers && subledgers.length > 1 && (
          <div className="flex flex-row items-center gap-2 pb-2">
            <span className="text-sm font-medium text-gray-700">{L.admin_panel.accounts.view}</span>
            <Select
              label={L.admin_panel.accounts.subledger}
              hideLabel
              description={L.admin_panel.accounts.filter_by_subledger}
              value={selectedSubledger}
              onChange={(value) => setSelectedSubledger(value as SelectOptionType)}
              options={subledgerOptions}
              className="w-48"
            />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {showTotalAvailableBalance && (
            <div className="lg:col-span-1">
              {totalBalanceCard}
            </div>
          )}
          {showPaymentProviderShareChart && (
            <div className={showTotalAvailableBalance ? "lg:col-span-3" : "lg:col-span-4"}>
              {accountsPieChartCard}
            </div>
          )}
        </div>
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-stretch">
          {accountDetailsCards}
        </div>

        {accountsDataTimestamp && (
          <div className="w-full flex flex-row">
            <div className="text-xs font-medium text-gray-500">
              <span>{L.admin_panel.payments.last_updated}:&nbsp;</span><DateDisplay className="text-gray-500 text-xs font-medium" utcValue={accountsDataTimestamp} showRelativeTime />
            </div>
          </div>
        )}
      </div>

      <ManagePaymentRemindersModal
        remindersModalOpen={manageBalanceRemindersModalOpen}
        setRemindersModalOpen={setManageBalanceRemindersModalOpen}
        availableReminderTypes={['account_balance']}
      />
    </AdminPanelPage>
  )
}